<template>
  <div class="locator-map">
    <onemap-search ref="search" placeholder="eg. 1000 Toa Payoh or 318994" :max-length="0"></onemap-search>
    <st-get-svg class="legend"
      :style="{opacity: 0.9, zIndex: 1}"
      root-folder="boundaries/locator-legend"
      platform="desktop">
    </st-get-svg>
    <div class="tooltip"></div>
  </div>
</template>

<script>
import Vue from 'vue'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

import OnemapSearch from './OnemapSearch'
import PopoverContent from './PopoverContent'
import TooltipContent from './TooltipContent'
import StGetSvg from '../SvgFolder/StGetSvg'

const SINGAPORE = {
  center: [103.819836, 1.352083],
  zoom: 11
}

export default {
  components: {
    OnemapSearch,
    StGetSvg
  },
  props: ['attributes'],
  mounted () {
    const map = new mapboxgl.Map(Object.assign({
      container: this.$el,
      style: 'mapbox://styles/chachopazos/ck3fc0qpp0fyd1ct5ai5owqu0',
      minZoom: 10,
      maxZoom: 12,
      maxBounds: [[103.42295489648154, 1.0774867904165433], [104.21671710351262, 1.6266481512753188]],
      scrollZoom: false,
      dragPan: false,
      dragRotate: false,
      accessToken: 'pk.eyJ1IjoiY2hhY2hvcGF6b3MiLCJhIjoiY2pkMDN3eW4wNHkwZDJ5bGc0cnpueGNxbCJ9.WWWg_OnK5e7L1RknMliY4A'
    }, SINGAPORE))
    map.touchZoomRotate.disableRotation()

    this.$watch('$window.innerWidth', w => {
      if (w >= 768) map.dragPan.enable()
      else map.dragPan.disable()
    }, {immediate: true})

    const nav = new mapboxgl.NavigationControl({showCompass: false})
    map.addControl(nav, 'top-left')

    const popover = createPopup(PopoverContent, {
      closeButton: false,
      closeOnClick: false
    }).on('close', () => popover.remove())
    const tooltip = createPopup(TooltipContent, {
      closeButton: false,
      closeOnClick: false,
      anchor: 'center'
    })

    map.on('load', () => {
      map.addSource('ge-boundaries', {
        type: 'vector',
        url: 'mapbox://chachopazos.ge-boundaries-new'
      })

      map.addLayer({
        id: 'ge-boundaries_fill',
        source: 'ge-boundaries',
        'source-layer': 'ge-boundaries-2020',
        type: 'fill',
        paint: {
          'fill-color': ['match',
            ['get', 'grc'],
            'SMC', 'rgba(25, 45, 86, 0.2)',
            '3-Member GRC', 'rgba(25, 45, 86, 0.1)',
            '4-Member GRC', 'rgba(25, 45, 86, 0.35)',
            '5-Member GRC', 'rgba(25, 45, 86, 0.60)',
            '6-Member GRC', 'rgba(25, 45, 86, 0.8)',
            'white'
          ],
          'fill-opacity': 0.9
        }
      })

      map.addLayer({
        id: 'ge-boundaries_outline',
        source: 'ge-boundaries',
        'source-layer': 'ge-boundaries-2020',
        type: 'line',
        paint: {
          'line-color': 'white',
          'line-width': 1.2
        }
      })

      map.loadImage(require('images/boundaries/pattern.png'), function (err, image) {
        if (err) throw err

        map.addImage('pattern', image)

        map.addLayer({
          id: 'ge-boundaries_pattern',
          source: 'ge-boundaries',
          'source-layer': 'ge-boundaries-2020',
          type: 'fill',
          filter: ['match',
            ['get', 'constituency'],
            // ['East Coast', 'Holland-Bukit Timah', 'Ang Mo Kio'], true,
            ['Kebun Baru', 'Sengkang', 'Marymount', 'Punggol West', 'Yio Chu Kang'], true,
            false
          ],
          paint: {
            'fill-pattern': 'pattern'
          }
        })
      })

      map.on('mousemove', e => {
        if (popover.isOpen()) return
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['ge-boundaries_fill']
        })
        if (features.length > 0) {
          tooltip.setData(features[0].properties.constituency).trackPointer().addTo(map)
        } else {
          tooltip.setData(null).remove()
        }
      })
    })

    this.$refs.search.$on('select', row => {
      const coordinates = [+row.LONGITUDE, +row.LATITUDE]
      map.flyTo({
        center: coordinates,
        zoom: 12
      })

      popover.setData(null).remove()
      setTimeout(() => {
        const features = map.queryRenderedFeatures(map.project(coordinates), {
          layers: ['ge-boundaries_fill']
        })
        if (features.length > 0) {
          popover.setData(features[0].properties).setLngLat(coordinates).addTo(map)
        }
      }, 300)
    })

    this.$refs.search.$on('clear', row => {
      map.flyTo(SINGAPORE)
      popover.setData(null).remove()
    })

    map.on('click', e => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['ge-boundaries_fill']
      })
      if (features.length > 0) {
        tooltip.setData(null).remove()
        popover.setData(features[0].properties).setLngLat(e.lngLat).addTo(map)
      } else {
        popover.setData(null).remove()
      }
    })
  }
}

function createPopup (Content, options) {
  const popup = new mapboxgl.Popup(options)
  const $el = document.createElement('div')
  const vm = new Vue(Content)
  popup.setDOMContent($el)
  vm.$mount($el)
  popup.setData = function (data) {
    vm.data = Object.freeze(data)
    return this
  }
  popup.on = function (event, callback) {
    vm.$on(event, callback)
    return this
  }
  return popup
}
</script>

<style lang="scss">
.locator-map {
  width: 100%;
  height: 800px;

  @media (max-width: $d_normal){
    height: 400px;
  }

  .onemap-search {
    display: block;
    position: absolute;
    z-index: 1;
    top: 20px;
    right: 20px;
  }

  .tooltip {
    position: absolute;
  }

  .legend {
    width: 240px;
    position: absolute;
    bottom: 20px;
    right: 20px;

    @media (max-width: $d_normal){
      bottom: 0;
    }
  }

  .mapboxgl-marker svg g g:nth-child(2) {
    fill: $stblue;
  }

  .mapboxgl-popup-content {
    padding: 3px 8px;
    font-family: $font-OpenSans-Regular;
  }

  .mapboxgl-control-container {
    .mapboxgl-ctrl-bottom-left,
    .mapboxgl-ctrl-bottom-right {
      display: none;
    }
  }
}
</style>
